import Pickup from '../../models/Pickup';
import Tracking from '../../models/Tracking';
import dayjs from 'root-js/plugins/Dayjs';
import { formatBoxDetails, normalizeBoxId } from '../../helpers/box.helper';
import { PICKUP_API_LOOKAHEAD_DAYS } from '../../constants/calendar.const';
import { BOX_STATE } from '../../constants/space.const';

const namespaced = true;
let latestStoreShippingRequestId = 0;

const hasBoxField = value => String(value || '').trim().length > 0;

const hasSchedulePickupDetails = box => {
    return hasBoxField(box?.title) && hasBoxField(box?.description);
};

const defaultStoreShipping = () => ({
  pickup_date: null,
  pickup_time: null,
  delivery_date: null,
  delivery_time_start: null,
  delivery_time_end: null,
  total_transit_days: 0,
  business_transit_days: 0,
  holiday_count: 0,
  name: null,
  key: null,
  provider: null,
  duration: null,
  cut_off_time: null,
  estimated_arrival: null,
  charge: 0,
  surcharge: 0,
});


const getInitialState = () => {
    return {
        generation: 0,
        boxes: [],
        boxTrackingById: {},
        boxTrackingRequestStatusById: {},
        storeShipping: defaultStoreShipping(),
        selectedBox: null,
        boxRequest: {
            signature_required: 0,
            saturday_delivery: 0,
            carrier_service: 'ground',
            _simulate: 1
        },
        pickupDetails: new Pickup,
        pickupDate: null,
        selectedDeliveryAddress: null,
        selectedPickupBoxIds: []
    };
};

const state = getInitialState();

const getters = {
    boxes: (state, getters) => state.boxes
        .map(box => {
            if (!box) {
                return null;
            }
            const tracking = getters.boxTrackingById[box.id] || null;
            return {
                ...box,
                tracking
            };
        })
        .filter(Boolean),
    boxRequest: state => {
        return {
            ...state.boxRequest,
            shipping_address: state.selectedDeliveryAddress?.full_address || ''
        };
    },
    boxTrackingById: state => state.boxTrackingById,
    boxTrackingRequestStatusById: state => state.boxTrackingRequestStatusById,
    selectedBox: (state, getters) => {
        const selectedBoxId = normalizeBoxId(state.selectedBox);
        if (selectedBoxId == null) {
            return undefined;
        }

        return getters.boxes.find(box => normalizeBoxId(box.id) === selectedBoxId);
    },
    selectedPickupBoxIds: state => state.selectedPickupBoxIds,
    pickupEligibleBoxes: (state, getters) => {
        return getters.boxes.filter(box => {
            if (box.state === BOX_STATE.SEND_TO_SPACE) {
                return true;
            }

            return box.state === BOX_STATE.CONTINUE_PACKING
                && hasSchedulePickupDetails(box);
        });
    },
    selectedPickupBoxes: (state, getters) => {
        const selectedIds = new Set(state.selectedPickupBoxIds.map(id => String(id)));
        return getters.boxes.filter(box => selectedIds.has(String(box.id)));
    },
    selectedDeliveryAddress: state => state.selectedDeliveryAddress,
    storeShipping: state => state.storeShipping,
    pickupDetails: state => state.pickupDetails,
    pickupDate: state => state.pickupDate
};

const mutations = {
    SET_BOXES: (state, payload) => {
        state.boxes = payload.map(box => formatBoxDetails(box));
    },
    ADD_BOX: (state, payload) => {
        state.boxes = [...state.boxes, formatBoxDetails(payload)];
    },
    UPDATE_BOX: (state, payload) => {
        const boxIndex = state.boxes.findIndex(
            box => normalizeBoxId(box.id) === normalizeBoxId(payload.id)
        );

        state.boxes[boxIndex] = payload;

        state.boxes = [ ...state.boxes ];
    },
    SET_SELECTED_BOX: (state, payload) => {
        state.selectedBox = normalizeBoxId(payload);
    },
    SET_SELECTED_PICKUP_BOX_IDS: (state, payload = []) => {
        state.selectedPickupBoxIds = [
            ...new Set(payload
                .map(normalizeBoxId)
                .filter(id => id !== null && id !== ''))
        ];
    },
    ENSURE_SELECTED_PICKUP_BOX_ID: (state, payload) => {
        if (!payload) {
            return;
        }
        const exists = state.selectedPickupBoxIds.some(id => String(id) === String(payload));
        if (!exists) {
            state.selectedPickupBoxIds = [
                ...state.selectedPickupBoxIds,
                normalizeBoxId(payload)
            ];
        }
    },
    TOGGLE_SELECTED_PICKUP_BOX_ID: (state, payload) => {
        if (!payload) {
            return;
        }
        const exists = state.selectedPickupBoxIds.some(id => String(id) === String(payload));
        state.selectedPickupBoxIds = exists
            ? state.selectedPickupBoxIds.filter(id => String(id) !== String(payload))
            : [...state.selectedPickupBoxIds, normalizeBoxId(payload)];
    },
    SET_DELIVERY_ADDRESS: (state, payload) => {
        
        state.selectedDeliveryAddress = payload;
    },
    SET_STORE_SHIPPING: (state, payload) => {
        state.storeShipping = {
            ...state.storeShipping,
            ...payload
        };
    },
     RESET_STORE_SHIPPING: (state) => {
    state.storeShipping = defaultStoreShipping();
  },
    SET_BOX_TRACKING: (state, { boxId, tracking }) => {
        state.boxTrackingById = {
            ...state.boxTrackingById,
            [boxId]: new Tracking(tracking)
        };
    },
    SET_BOX_TRACKING_REQUEST_STATUS: (state, { boxId, status }) => {
        state.boxTrackingRequestStatusById = {
            ...state.boxTrackingRequestStatusById,
            [boxId]: status
        };
    },
    CLEAR_DELIVERY_ADDRESS: (state, payload) => {
        state.selectedDeliveryAddress = null;
    },
    REMOVE_BOX_BY_ID: (state, payload) => {
        state.generation += 1;
        const boxId = normalizeBoxId(payload);
        state.boxes = state.boxes.filter(box => normalizeBoxId(box.id) !== boxId);
    },
    SET_PICKUP_DETAILS: (state, payload) => {
        state.pickupDetails = new Pickup(payload);
    },
    SET_PICKUP_DATE: (state, payload) => {
        state.pickupDate = payload;
    },
    RESET_PICKUP_SCHEDULING: state => {
        state.pickupDetails = new Pickup();
        state.pickupDate = null;
        state.selectedPickupBoxIds = [];
    },
    RESET_STATE: state => {
        const generation = state.generation + 1;
        Object.assign(state, getInitialState());
        state.generation = generation;
    }
};

const actions = {
    GET_BOXES: async context => {
        const generation = context.state.generation;
        try {
            const response = await window.api.call('get-boxes');

            const boxes = response.data.data.sort((a, b) => new Date(a.created_at) < new Date(b.created_at) ? 1 : -1);

            if (generation === context.state.generation) {
                context.commit('SET_BOXES', boxes);
            }

            return boxes;
        } catch (error) {
            console.error('Error fetching boxes:', error);
        }
    },
    DELETE_BOX_BY_ID: async (context, payload) => {
        const response = await window.api.call('delete-box', { id: payload });

        context.commit('REMOVE_BOX_BY_ID', payload);

        return response.data.data;
    },
    GET_STORE_SHIPPING: async (context, payload) => {
        const requestId = ++latestStoreShippingRequestId;
        try {
            const response = await window.api.call('get-shipping-duration', {
                address: payload.address_id,
                box: payload.box_id,
                type: payload.type || 'box_request',
                ship_date: payload.ship_date
            });

            // Ignore stale responses when multiple address selections happen quickly.
            if (requestId === latestStoreShippingRequestId) {
                context.commit('SET_STORE_SHIPPING', response.data.data);
            }

            return response.data.data;
        } catch (error) {
            console.error('Error fetching shipping duration:', error);

            // Reset only for latest request; stale errors should not override fresh state.
            if (requestId === latestStoreShippingRequestId) {
                context.commit('SET_STORE_SHIPPING', {
                    pickup_date: null,
                    pickup_time: null,
                    delivery_date: null,
                    delivery_time_start: null,
                    delivery_time_end: null,
                    total_transit_days: 0,
                    business_transit_days: 0,
                    holiday_count: 0
                });
            }
            throw error; // Re-throw the error to be caught by the component
        }
    },
    GET_BOX_TRACKING_BY_ID: async (context, payload) => {
        if (!payload) {
            return;
        }

        context.commit('SET_BOX_TRACKING_REQUEST_STATUS', {
            boxId: payload,
            status: 'pending'
        });

        try {
            const response = await window.api.call('get-box-tracking', { id: payload });

            const tracking = response.data.data;

            
            


            if (tracking && tracking.length > 0) {
                context.commit('SET_BOX_TRACKING', {
                    boxId: payload,
                    tracking: tracking[tracking.length - 1] || {}
                });
            } else {
                context.commit('SET_BOX_TRACKING', {
                    boxId: payload,
                    tracking: {}
                });
            }

            return tracking;
        } catch (error) {
            console.error('Error fetching box tracking:', error);

            // Set empty tracking object on error to prevent undefined access
            context.commit('SET_BOX_TRACKING', {
                boxId: payload,
                tracking: {}
            });
        } finally {
            context.commit('SET_BOX_TRACKING_REQUEST_STATUS', {
                boxId: payload,
                status: 'completed'
            });
        }
    },
    UPDATE_BOX_BY_ID: async (context, payload) => {
        const response = await window.api.call('update-box', {
            id: payload.id,
            ...payload
        });

        context.commit('UPDATE_BOX', formatBoxDetails(response.data.data));

        return response.data.data;
    },
    GET_PICKUP_DETAILS_BY_ADDRESS_ID: async (context, payload) => {
        
        try {
            const response = await window.api.call('get-box-pickup', {
                start_date: dayjs().format('YYYY-MM-DD'),
                end_date: dayjs()
                    .add(PICKUP_API_LOOKAHEAD_DAYS, 'day')
                    .format('YYYY-MM-DD'),
                carrier: 'ups',
                origin_address: payload
            });

            const pickupDetails = response.data.data[0];

            context.commit('SET_PICKUP_DETAILS', pickupDetails);

            return pickupDetails;
        } catch (error) {
            console.error('Error fetching pickup details:', error);
            context.commit('SET_DELIVERY_ADDRESS', null);
        }
    },
    GET_SHIPPING: async (context, payload) => {
        try {
            const response = await window.api.call('get-shipping', {
                shipment_type: payload.shipment_type,
                item_id: payload.item_id
            });

            context.commit('SET_STORE_SHIPPING', response.data.data.GROUND);
            return response.data.data.GROUND;
        } catch (error) {
            console.error('Error fetching shipping:', error);
            throw error;
        }
    }
};

export default {
    namespaced,
    state,
    mutations,
    actions,
    getters
};
